import React, { useEffect, useRef } from 'react';
import { CheckCircle2 } from 'lucide-react';

const HeroSection = ({ onBookClick }) => {
  const heroRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.fade-up').forEach((el, index) => {
              setTimeout(() => { el.classList.add('visible'); }, index * 100);
            });
          }
        });
      },
      { threshold: 0.1 }
    );
    if (heroRef.current) observer.observe(heroRef.current);
    return () => observer.disconnect();
  }, []);

  const handleScrollToHow = (e) => {
    e.preventDefault();
    const element = document.querySelector('#how-it-works');
    if (element) {
      const offset = 80;
      const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
      window.scrollTo({ top: elementPosition - offset, behavior: 'smooth' });
    }
  };

  return (
    <section
      id="home"
      ref={heroRef}
      className="relative min-h-screen flex items-center overflow-hidden"
      style={{
        /* Dark overlay (~70%) so the photo reads as rich texture, not a washed-out wash.
           The dark header flows directly into this, creating a single confident dark banner. */
        background: `linear-gradient(rgba(0,0,30,0.70) 0%, rgba(0,0,30,0.62) 100%), url('https://images.pexels.com/photos/8527647/pexels-photo-8527647.jpeg?auto=compress&cs=tinysrgb&w=1920')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
    >
      {/* Subtle mint radial glow — adds warmth without breaking the dark feel */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: 'radial-gradient(ellipse at 20% 60%, rgba(128,229,203,0.08) 0%, transparent 55%)',
        }}
      />

      {/* Floating mint dots */}
      <div className="mint-dot" style={{ top: '18%', right: '12%', width: '18px', height: '18px', opacity: 0.5 }} />
      <div className="mint-dot" style={{ bottom: '22%', left: '8%', width: '14px', height: '14px', opacity: 0.35 }} />
      <div className="mint-dot" style={{ top: '45%', right: '28%', width: '10px', height: '10px', opacity: 0.25 }} />

      <div className="container relative z-10 py-32">
        <div className="max-w-3xl">

          {/* Pill — mint text on dark bg */}
          <div className="fade-up">
            <span className="pill-label-dark">Brisbane, Sydney &amp; Melbourne</span>
          </div>

          {/* Logo */}
          <div className="fade-up my-6">
            <img
              src="/Nura_Final-04.png"
              alt="Nura Health mobile pathology logo"
              style={{ height: '80px', width: 'auto', background: 'transparent', objectFit: 'contain' }}
            />
          </div>

          {/* Main headline — white on dark */}
          <h1
            className="fade-up mb-6"
            style={{ color: '#FFFFFF', lineHeight: '1.1' }}
          >
            Blood collection,<br />at your door.
          </h1>

          {/* Subtext */}
          <p
            className="fade-up text-lg md:text-xl mb-10 max-w-2xl leading-relaxed"
            style={{ color: 'rgba(255,255,255,0.82)' }}
          >
            We send a qualified phlebotomist to you — at home, at work, or at your aged care
            facility. No waiting rooms. No parking. Just care, at your door.
          </p>

          {/* CTAs */}
          <div className="fade-up flex flex-col sm:flex-row gap-4 mb-14">
            {/* Primary — mint */}
            <button
              onClick={onBookClick}
              style={{
                background: '#80E5CB',
                color: '#00001E',
                border: 'none',
                borderRadius: '8px',
                padding: '15px 30px',
                fontWeight: '700',
                fontSize: '15px',
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
                fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                boxShadow: '0 4px 20px rgba(128,229,203,0.40)',
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 10px 36px rgba(128,229,203,0.55)';
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 4px 20px rgba(128,229,203,0.40)';
              }}
            >
              Book a Collection
            </button>

            {/* Secondary — white outline on dark bg */}
            <button
              onClick={handleScrollToHow}
              style={{
                background: 'transparent',
                color: '#FFFFFF',
                border: '2px solid rgba(255,255,255,0.40)',
                borderRadius: '8px',
                padding: '13px 28px',
                fontWeight: '500',
                fontSize: '15px',
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
                fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
                cursor: 'pointer',
                transition: 'all 0.3s ease',
              }}
              onMouseEnter={(e) => {
                e.target.style.background = 'rgba(255,255,255,0.12)';
                e.target.style.borderColor = 'rgba(255,255,255,0.70)';
                e.target.style.transform = 'translateY(-2px)';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = 'transparent';
                e.target.style.borderColor = 'rgba(255,255,255,0.40)';
                e.target.style.transform = 'translateY(0)';
              }}
            >
              How it works
            </button>
          </div>

          {/* Trust row — white text, mint icons */}
          <div
            className="fade-up grid grid-cols-1 md:grid-cols-3 gap-6 pt-8"
            style={{ borderTop: '1px solid rgba(255,255,255,0.15)' }}
          >
            {[
              'Cert III Qualified Collectors',
              'NPAAC Compliant',
              'All Request Forms Accepted',
            ].map((item) => (
              <div key={item} className="flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: '#80E5CB' }} />
                <p className="text-sm font-medium" style={{ color: 'rgba(255,255,255,0.90)' }}>
                  {item}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Scroll indicator — white/mint */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
        <div
          className="w-6 h-10 rounded-full flex justify-center pt-2"
          style={{ border: '2px solid rgba(255,255,255,0.35)' }}
        >
          <div className="w-1 h-3 rounded-full" style={{ background: '#80E5CB' }} />
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
